import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  ISurveySyncComparison,
  ISurveySyncResult,
  SurveySyncStatus,
} from "../../../@types/admin/AdminSurveySync";
import { TableSorted } from "../../../components/TableSorted";
import { useSyncSurveys } from "../../../hooks/mutations/admin/surveys";
import {
  surveysSyncComparisonKey,
  useSurveysSyncComparison,
} from "../../../hooks/queries/admin/surveys";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { getEnvironment, isProduction } from "../../../utils/environment";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  PageContainer,
  PageDescription,
  PageHeader,
  PageTitle,
  StatCard,
  StatLabel,
  StatsGrid,
  StatusBadge,
  StatValue,
  TableContainer,
  TableLoadingState,
  TableWrapper,
  WarningNotice,
} from "../shared";
import {
  ModalBodyText,
  ResultNotice,
} from "../Translations/AdminTranslations.styles";

const STATUSES: SurveySyncStatus[] = ["InGlobal", "Missing", "Differs"];

const STATUS_LABEL: Record<SurveySyncStatus, string> = {
  InGlobal: "In Global",
  Missing: "Missing",
  Differs: "Differs",
};

const STATUS_BADGE: Record<SurveySyncStatus, "active" | "error" | "warning"> = {
  InGlobal: "active",
  Missing: "error",
  Differs: "warning",
};

const pair = (regional: string, global: string | null | undefined) =>
  `${regional} / ${global ?? "-"}`;

const describeResult = (r: ISurveySyncResult) =>
  `${r.surveysInserted} surveys inserted, ${r.surveysAlreadyInGlobal} already in Global, ` +
  `${r.definitionRowsInserted} definition rows, ${r.historyRowsInserted} history rows, ` +
  `${r.nameTranslationsSuffixed} name translations suffixed`;

interface IConfirmSurveySyncModalProps {
  missingCount: number;
  environmentName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

function ConfirmSurveySyncModal({
  missingCount,
  environmentName,
  onConfirm,
  onClose,
}: IConfirmSurveySyncModalProps) {
  const { showToast } = useToast();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleConfirm = async () => {
    setIsSyncing(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "The sync failed. Nothing was changed.",
        "warning"
      );
      setIsSyncing(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Sync surveys to Global?</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Copies <strong>{missingCount}</strong> missing survey
          {missingCount === 1 ? "" : "s"} from this region into the{" "}
          <strong>{environmentName}</strong> Global database.
        </ModalBodyText>
        {isProduction() && <WarningNotice>This is PRODUCTION.</WarningNotice>}
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSyncing}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSyncing}
            onClick={handleConfirm}
          >
            {isSyncing ? "Syncing..." : "Sync"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}

/**
 * Survey sync (EN-5261). Survey definitions move from the regional DBs to
 * Global; each regional API copies its OWN region's surveys. Temporary —
 * delete with the API endpoint once every region has been synced.
 */
export function AdminSurveySync() {
  document.title = "Survey sync - Admin";

  const { openModal, closeModal } = useModal();
  const environmentName = getEnvironment();
  const { comparison, isLoading, isError } = useSurveysSyncComparison();
  const { syncSurveys, isSyncing } = useSyncSurveys();
  const [result, setResult] = useState<ISurveySyncResult | null>(null);

  const counts = useMemo(
    () =>
      Object.fromEntries(
        STATUSES.map((s) => [
          s,
          comparison.filter((row) => row.status === s).length,
        ])
      ) as Record<SurveySyncStatus, number>,
    [comparison]
  );

  const handleSync = () => {
    openModal(
      <ConfirmSurveySyncModal
        missingCount={counts.Missing}
        environmentName={environmentName}
        onConfirm={async () => setResult(await syncSurveys())}
        onClose={closeModal}
      />,
      { width: "520px", showCloseButton: true }
    );
  };

  const columns: ColumnDef<ISurveySyncComparison, string>[] = useMemo(
    () => [
      { accessorKey: "name", header: "Name", enableSorting: true },
      {
        id: "regionalCode",
        header: "Regional code",
        accessorFn: (r) => r.regionalCode ?? "-",
        enableSorting: true,
      },
      {
        id: "globalCode",
        header: "Global code",
        accessorFn: (r) => r.globalCode ?? "-",
        enableSorting: true,
      },
      {
        id: "questions",
        header: "Questions (regional / global)",
        accessorFn: (r) =>
          pair(
            String(r.regionalQuestionCount),
            r.globalQuestionCount?.toString()
          ),
        enableSorting: false,
      },
      {
        id: "lastModified",
        header: "Last modified (regional / global)",
        accessorFn: (r) =>
          pair(
            getLongFormattedDate(r.regionalLastModified),
            r.globalLastModified && getLongFormattedDate(r.globalLastModified)
          ),
        enableSorting: false,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => STATUS_LABEL[r.status],
        cell: ({ row }) => (
          <StatusBadge status={STATUS_BADGE[row.original.status]}>
            {STATUS_LABEL[row.original.status]}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "differences",
        header: "Differences",
        accessorFn: (r) => r.differences.join(", "),
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Survey sync</PageTitle>
      </PageHeader>

      <PageDescription>
        Copies this region&apos;s surveys into the Global database. Only surveys
        missing from Global are inserted; existing ones are never overwritten,
        so it is safe to re-run. US surveys get a &quot;_us&quot; code suffix
        and a &quot; (US)&quot; name suffix.
      </PageDescription>

      <StatsGrid>
        {STATUSES.map((s) => (
          <StatCard key={s}>
            <StatLabel>{STATUS_LABEL[s]}</StatLabel>
            <StatValue aria-label={`${STATUS_LABEL[s]}: ${counts[s]}`}>
              {counts[s]}
            </StatValue>
          </StatCard>
        ))}
      </StatsGrid>

      <FiltersContainer>
        <FiltersActions>
          <FilterButton
            variant="primary"
            disabled={isSyncing || isLoading}
            onClick={handleSync}
          >
            {isSyncing ? "Syncing..." : "Sync to Global"}
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {result && (
        <ResultNotice>Sync finished: {describeResult(result)}.</ResultNotice>
      )}

      {isError && (
        <ErrorMessage>
          Failed to load the survey comparison. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[surveysSyncComparisonKey]}>
          <TableSorted
            columns={columns}
            data={comparison}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No surveys found"
            }
          />
        </TableWrapper>
      </TableContainer>
    </PageContainer>
  );
}
